import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Stack, Typography} from '@mui/material';

import {clientDetailsQuery} from '@store/clients/details';
import {UnitOfMeasure} from '@store/settings/serviceKinds';
import {ManagePrice} from '@store/common/price.model';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import ModalComponent from '@components/modal/Modal.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {TaxRule} from '@store/common/organisation.enums';
import {NumberExt} from '@utils/extensions/number.extension';
import {West} from '@mui/icons-material';

interface ManagePriceModalProps {
  handleClose: () => void;
  handleSaved: (price: ManagePrice) => void;
  price?: ManagePrice;
  unit?: UnitOfMeasure;
  minDate?: Date;
  outTaxRule?: TaxRule;
  currentPrice?: ManagePrice;
  newPrice?: number;
}

const ManagePriceModal = (props: ManagePriceModalProps) => {
  const {handleClose, handleSaved, price: priceProps, minDate, outTaxRule, currentPrice, newPrice} = props;

  const {t} = useTranslation();

  const [loading, setLoading] = useState(false);

  const [price, setPrice] = useState<ManagePrice>(priceProps || {});
  const [tax] = useState<number>(outTaxRule === TaxRule.ICO ? 1.08 : 1.2);

  useEffect(() => {
    if (!priceProps && !newPrice) {
      setPrice({currency: clientDetailsQuery.clientCurrency});
    } else if (newPrice) {
      setPrice({
        ...price,
        unitCostInCents: newPrice,
        unitCost: newPrice / 100,
        wasUpdated: true,
        unitPriceInCents: Math.round(newPrice * tax),
        unitPrice: Math.round(newPrice * tax) / 100,
        validFromDate: new Date()
      },);
    }
  }, [priceProps]);

  const errors = {
    unitCost: getFieldError(price.unitCost, FieldValidationType.REQUIRED_NUMBER),
    unitPrice: getFieldError(price.unitPrice, FieldValidationType.REQUIRED_NUMBER)
  };

  return (
    <ModalComponent
      aria-label="over-drawer"
      handleClose={handleClose}
      title={t(`clients.details.contractedServices.manage.managePrice.title.${!priceProps ? 'add' : 'edit'}`)}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="10px">
          <Stack direction="row" alignItems="center" spacing={2}>
            <NumberTextFieldComponent
              color="secondary"
              labelWidth={160}
              label={t('clients.details.contractedServices.manage.puBuy').replaceAll('\n', ' ')}
              placeholder={t('global.writeSomething')}
              pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
              handleChange={(unitCost) => setPrice((state) => ({
                ...state,
                unitCost: unitCost,
                unitCostInCents: Math.round((unitCost || 0) * 100),
                unitPrice: ((unitCost || 0) * tax),
                unitPriceInCents: Math.round((((unitCost || 0) * tax)) * 100),
                wasUpdated: state.id != null
              }))}
              endAdornment={(
                <Typography fontSize={14} fontWeight="400">
                  {price.currency}
                </Typography>
              )}
              value={price.unitCost ?? undefined}
              error={errors.unitCost}
              required/>
            <West/>
            <Typography fontSize={15}>{NumberExt.toCurrency(currentPrice?.unitCost || 0)}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <NumberTextFieldComponent
              color="secondary"
              labelWidth={160}
              label={t('clients.details.contractedServices.manage.puSell').replaceAll('\n', ' ')}
              placeholder={t('global.writeSomething')}
              pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
              handleChange={(unitCost) => setPrice((state) => ({
                ...state,
                unitPrice: unitCost,
                unitPriceInCents: Math.round((unitCost || 0) * 100),
                wasUpdated: state.id != null
              }))}
              endAdornment={(
                <Typography fontSize={14} fontWeight="400">
                  {price.currency}
                </Typography>
              )}
              value={price.unitPrice ?? undefined}
              error={errors.unitPrice}
              required/>
            <West/>
            <Typography fontSize={15}>{NumberExt.toCurrency(currentPrice?.unitPrice || 0)}</Typography>
          </Stack>
          <DatePickerComponent
            color="secondary"
            labelWidth={160}
            label={t('clients.details.contractedServices.manage.validityPrice')}
            placeholder={t('global.selectDate')}
            minDate={minDate}
            value={price.validFromDate}
            inputWidth={120}
            handleChange={(validFromDate) => setPrice((state) => ({...state, validFromDate}))}
            required
          />
        </Stack>
      )}
      actions={(
        <>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {t('global.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={loading || checkIfErrors(errors)}
            onClick={() => {
              setLoading(true);
              handleSaved(price);
              handleClose();
            }}>
            {t('global.save')}
          </Button>
        </>
      )}/>
  );
};

export default ManagePriceModal;
